import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface ItemResourceResponse {
    resource_id?: number | null;
    resource_itemlots_code?: string | null;
    resource_type?: string | null;
    resource_path?: string | null;
    resource_sort_order?: number | null;
    resource_title?: string | null;
    resource_crby?: string | null;
    resource_crdate?: string | null;
    resource_mdby?: string | null;
    resource_mddate?: string | null;
    is_approved?: number | null;
    is_primary?: number | null;
}

export interface ItemResourceCreateUpdateRequest {
    resource_itemlots_code: string;
    resource_type: string;
    resource_path: string;
    resource_sort_order?: number | null;
    resource_title?: string | null;
}

export const getAllItemResources = async (): Promise<ItemResourceResponse[]> => {
    try {
        const response: ApiResponse<ItemResourceResponse[] | null> = (
            await apiClient.get("/pos_item_resource/all", {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.status === 401) {
            throw new Error("Not authorized to load item resources. Please sign in with backend authentication.");
        }

        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to fetch item resources");
    }
};

export const getResourcesByItem = async (itemlotsCode: string): Promise<ItemResourceResponse[]> => {
    try {
        const response: ApiResponse<ItemResourceResponse[] | null> = (
            await apiClient.get(`/pos_item_resource/by-item/${encodeURIComponent(itemlotsCode)}`, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to fetch item resources");
    }
};

export const createItemResource = async (data: ItemResourceCreateUpdateRequest): Promise<ItemResourceResponse> => {
    try {
        const response: ApiResponse<ItemResourceResponse> = (
            await apiClient.post("/pos_item_resource/create", data, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Failed to create item resource - no data returned");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to create item resource");
    }
};

export const updateItemResource = async (resourceId: number, data: ItemResourceCreateUpdateRequest): Promise<ItemResourceResponse> => {
    try {
        const response: ApiResponse<ItemResourceResponse> = (
            await apiClient.put(`/pos_item_resource/update/${resourceId}`, data, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Failed to update item resource - no data returned");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to update item resource");
    }
};

export const deleteItemResource = async (resourceId: number): Promise<void> => {
    try {
        await apiClient.delete(`/pos_item_resource/delete/${resourceId}`, {
            headers: {
                "x-skip-auth-redirect": "true",
            },
        });
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to delete item resource");
    }
};

export const uploadResourceFile = async (file: File, resourceType: "image" | "video" = "image", itemlotsCode: string = ""): Promise<string> => {
    try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("resource_type", resourceType);
        formData.append("itemlots_code", itemlotsCode);

        const response: ApiResponse<string> = (
            await apiClient.post("/pos_item_resource/upload", formData, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Failed to upload file - no path returned");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to upload file");
    }
};

export const syncItemResourcesForApproval = async (itemlotsCode: string): Promise<{ synced_count: number }> => {
    try {
        const response: ApiResponse<{ synced_count: number }> = (
            await apiClient.put(`/pos_item_resource/sync/${encodeURIComponent(itemlotsCode)}`, {}, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Failed to sync resources - no data returned");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to sync resources for approval");
    }
};

export const syncAllPendingResourcesForApproval = async (): Promise<{ synced_count: number; items_count: number }> => {
    try {
        const response: ApiResponse<{ synced_count: number; items_count: number }> = (
            await apiClient.post("/pos_item_resource/sync-all-pending", {}, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Failed to sync all pending resources - no data returned");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to sync all pending resources for approval");
    }
};

export const getAllPendingResources = async (): Promise<ItemResourceResponse[]> => {
    try {
        const response: ApiResponse<ItemResourceResponse[] | null> = (
            await apiClient.get("/pos_item_resource/pending/all", {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to fetch pending resources");
    }
};

export const approveItemResource = async (resourceId: number): Promise<ItemResourceResponse> => {
    try {
        const response: ApiResponse<ItemResourceResponse> = (
            await apiClient.put(`/pos_item_resource/approve/${resourceId}`, {}, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Failed to approve resource - no data returned");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to approve item resource");
    }
};

export const setPrimaryItemResource = async (resourceId: number): Promise<ItemResourceResponse> => {
    try {
        const response: ApiResponse<ItemResourceResponse> = (
            await apiClient.put(`/pos_item_resource/set-primary/${resourceId}`, {}, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Failed to set main image - no data returned");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to set main image");
    }
};

export const clearPrimaryItemResource = async (resourceId: number): Promise<ItemResourceResponse> => {
    try {
        const response: ApiResponse<ItemResourceResponse> = (
            await apiClient.put(`/pos_item_resource/clear-primary/${resourceId}`, {}, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Failed to unset main image - no data returned");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to unset main image");
    }
};